/**
 * 提现信息服务端保存工具（2026-08-15）
 *
 * 需求：提现渠道、平台结算的提现信息均走服务端保存，避免用户重复输入。
 * 统一封装两个新接口：
 *   POST api/trade/card/tx/bindInfo/save  — 保存
 *   GET  api/trade/card/tx/bindInfo        — 查询（按 bindType + bizKey）
 *
 * 绑定类型 bindType：1 自提现渠道；2 平台结算。
 * bizKey：自提现 = channelId；平台结算 = country 或 country+bank。
 * channelId：自提现渠道绑定传（与 bizKey 并存）；平台结算不传（=0）。
 * infoJson：客户端填什么存什么（JSON 字符串），后端不校验结构。
 *
 * 获取接口返回 BindInfoVO 对象：{ infoJson, channelName, arrivalTime }。
 */
import axios from "axios";

/** 绑定类型常量：1 自提现渠道 */
export const BIND_TYPE_WITHDRAW = 1;
/** 绑定类型常量：2 平台结算 */
export const BIND_TYPE_SETTLEMENT = 2;

/**
 * 保存提现绑定信息
 * @param {object} opts 参数
 * @param {number} opts.bindType 绑定类型 1自提现渠道 2平台结算
 * @param {string} opts.bizKey 绑定键（自提现=channelId；平台结算=country 或 country+bank）
 * @param {number} [opts.channelId=0] 渠道 id（自提现渠道绑定传，与 bizKey 并存；平台结算不传=0）
 * @param {object|string} opts.info 提现信息对象，将 JSON 序列化后存储
 * @param {string} opts.serviceIp 接口服务地址（this.$serviceIp）
 * @param {string} opts.token 登录令牌
 * @param {string} opts.uid 用户 id
 * @param {string} opts.deviceId 设备 id（请求头）
 * @param {string} opts.lang 语言（请求头）
 * @returns {Promise<boolean>} 是否保存成功
 */
export function saveBindInfo({ bindType, bizKey, channelId = 0, info, serviceIp, token, uid, deviceId, lang }) {
  return axios({
    url: serviceIp + "api/trade/card/tx/bindInfo/save",
    method: "post",
    headers: {
      deviceId,
      lang,
      version: "3.0",
    },
    data: {
      bindType,
      bizKey,
      channelId,
      infoJson: typeof info === "string" ? info : JSON.stringify(info || {}),
    },
    params: { token, uid },
  }).then((res) => res.data && res.data.code === 200);
}

/**
 * 获取用户已保存的提现绑定信息
 * @param {object} opts 参数
 * @param {number} opts.bindType 绑定类型 1自提现渠道 2平台结算
 * @param {string} opts.bizKey 绑定键
 * @param {string} opts.serviceIp 接口服务地址（this.$serviceIp）
 * @param {string} opts.token 登录令牌
 * @param {string} opts.uid 用户 id
 * @param {string} opts.deviceId 设备 id（请求头）
 * @param {string} opts.lang 语言（请求头）
 * @returns {Promise<{infoJson: object, channelName?: string, arrivalTime?: string}|null>}
 *   BindInfoVO：infoJson 为已解析的提现信息对象；无记录或异常时返回 null
 */
export function loadBindInfo({ bindType, bizKey, serviceIp, token, uid, deviceId, lang }) {
  return axios
    .get(serviceIp + "api/trade/card/tx/bindInfo", {
      params: { bindType, bizKey, token, uid },
      // version 请求头与其他接口保持一致（3.0）
      headers: { deviceId, lang, version: "3.0" },
    })
    .then((res) => {
      // data 为 BindInfoVO：{ infoJson, channelName, arrivalTime }；无记录时 data 为 null
      if (res.data && res.data.code === 200 && res.data.data) {
        const vo = res.data.data;
        let infoJson = null;
        try {
          infoJson = JSON.parse(vo.infoJson || "{}");
        } catch (e) {
          infoJson = vo.infoJson || null;
        }
        return {
          infoJson,
          channelName: vo.channelName || "",
          arrivalTime: vo.arrivalTime || "",
        };
      }
      return null;
    })
    .catch(() => null);
}
